// Ethers v5 Signer that wraps any viem WalletClient (Privy embedded wallet, local account in tests)
import { Signer, providers, utils } from 'ethers'
import { toHex } from 'viem'
import type { WalletClient } from 'viem'

export class ViemEthersSigner extends Signer {
	private walletClient: WalletClient
	private _address: `0x${string}`
	public provider: providers.Provider | undefined

	constructor(walletClient: WalletClient, provider?: providers.Provider) {
		super()
		this.walletClient = walletClient

		if (!walletClient.account) {
			throw new Error('WalletClient must have an account')
		}

		this._address = walletClient.account.address
		this.provider = provider

		this.getAddress = this.getAddress.bind(this)
		this.signMessage = this.signMessage.bind(this)
		this.signTransaction = this.signTransaction.bind(this)
	}

	async getAddress(): Promise<string> {
		return this._address
	}

	async signMessage(message: string | Uint8Array): Promise<string> {
		if (!this.walletClient.account) {
			throw new Error('No account available')
		}

		const formattedMessage = typeof message === 'string' ? message : toHex(message)

		const signature = await this.walletClient.signMessage({
			account: this.walletClient.account,
			message: formattedMessage,
		})

		return signature
	}

	async _signTypedData(
		domain: unknown,
		types: Record<string, unknown>,
		value: unknown
	): Promise<string> {
		if (!this.walletClient.account) {
			throw new Error('No account available')
		}

		// EIP712Domain must be excluded from types
		// biome-ignore lint/style/useNamingConvention: Intentionally unused with underscore
		const { EIP712Domain: _, ...typesWithoutDomain } = types
		const signature = await this.walletClient.signTypedData({
			account: this.walletClient.account,
			// biome-ignore lint/suspicious/noExplicitAny: EIP712 domain requires flexible typing
			domain: domain as any,
			// biome-ignore lint/suspicious/noExplicitAny: EIP712 types require flexible typing
			types: typesWithoutDomain as any,
			primaryType: Object.keys(typesWithoutDomain)[0],
			// biome-ignore lint/suspicious/noExplicitAny: EIP712 message requires flexible typing
			message: value as any,
		})
		return signature
	}

	async signTransaction(transaction: providers.TransactionRequest): Promise<string> {
		if (!this.walletClient.account) {
			throw new Error('No account available')
		}

		// Get nonce if not provided
		let nonce = transaction.nonce !== undefined ? Number(transaction.nonce) : undefined
		if (nonce === undefined && this.provider) {
			nonce = await this.provider.getTransactionCount(this._address, 'pending')
		}

		// Get gas price if not provided
		let gasPrice = transaction.gasPrice
		if (!gasPrice && this.provider) {
			const feeData = await this.provider.getFeeData()
			gasPrice = feeData.gasPrice || undefined
		}

		// Estimate gas if not provided
		let gasLimit = transaction.gasLimit
		if (!gasLimit && this.provider && transaction.to) {
			try {
				const estimated = await this.provider.estimateGas({
					from: this._address,
					to: transaction.to,
					data: transaction.data,
					value: transaction.value,
				})
				gasLimit = estimated.mul(120).div(100)
			} catch {
				gasLimit = 500000
			}
		}

		const chainId =
			transaction.chainId !== undefined ? Number(transaction.chainId) : this.walletClient.chain?.id

		const unsignedTx: utils.UnsignedTransaction = {
			to: transaction.to,
			value: transaction.value || 0,
			data: transaction.data || '0x',
			gasLimit,
			gasPrice,
			nonce,
			chainId,
			type: 0,
		}

		const serializedUnsigned = utils.serializeTransaction(unsignedTx)
		const txDigest = utils.keccak256(serializedUnsigned)

		const account = this.walletClient.account

		let signature: `0x${string}`

		if ('sign' in account && typeof account.sign === 'function') {
			// biome-ignore lint/suspicious/noExplicitAny: Turnkey account has dynamic sign method
			signature = await (account as any).sign({ hash: txDigest as `0x${string}` })
		} else {
			throw new Error('Account does not have sign method - cannot sign raw hash')
		}

		// Check if result is a raw signature (65 bytes = 132 hex chars with 0x prefix)
		const firstByte = Number.parseInt(signature.slice(2, 4), 16)
		const isRlpEncoded = firstByte >= 0xc0 || firstByte === 0x01 || firstByte === 0x02

		if (!isRlpEncoded && signature.length === 132) {
			const r = `0x${signature.slice(2, 66)}`
			const s = `0x${signature.slice(66, 130)}`
			const v = Number.parseInt(signature.slice(130, 132), 16)

			// Adjust v for EIP-155
			let adjustedV = v
			if (v === 0 || v === 1) {
				adjustedV = chainId ? chainId * 2 + 35 + v : 27 + v
			} else if (v === 27 || v === 28) {
				adjustedV = chainId ? chainId * 2 + 35 + (v - 27) : v
			}

			const signedTx = utils.serializeTransaction(unsignedTx, {
				r,
				s,
				v: adjustedV,
			})

			return signedTx
		}

		return signature
	}

	async sendTransaction(
		transaction: providers.TransactionRequest
	): Promise<providers.TransactionResponse> {
		if (!this.provider) {
			throw new Error('Provider not initialized')
		}
		const signedTx = await this.signTransaction(transaction)

		const parsedTx = utils.parseTransaction(signedTx)
		const recoveredAddress = parsedTx.from

		if (recoveredAddress?.toLowerCase() !== this._address.toLowerCase()) {
			throw new Error(
				`Signature recovery mismatch: expected ${this._address}, got ${recoveredAddress}`
			)
		}

		return this.provider.sendTransaction(signedTx)
	}

	connect(provider: providers.Provider): ViemEthersSigner {
		if (!(provider instanceof providers.JsonRpcProvider)) {
			throw new Error('Provider must be a JsonRpcProvider')
		}
		return new ViemEthersSigner(this.walletClient, provider)
	}
}
